// js/views/nexus.ts — Nexus kanıt-temelli durum panosu (5 bölüm).
// Kaynak: GET /castintech-api/nexus/report. Token-pure, MOR YOK.
import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

interface Evidence { kind: string; ref: string; at: string | null; detail?: string; }
interface Health { status: 'active' | 'degraded' | 'unknown'; reasons: string[]; evidence: Evidence[]; }
interface Blocker {
  issue_id: string; db_id: string | null; display_key: string; external_key: string | null;
  owner: string; reason: string; age: string; age_ms: number;
  waiting_on: string; last_comment_at: string | null; next_action: string; escalation_needed: boolean;
}
interface Handoff { at: string; title: string; from: string; detail: string; issue: string | null; }
interface CouncilRow { id: string; decision_type: string; subject_type: string; risk_level: string; status: string; created_at: string; }
interface BoardRow { id: string; type: string; title: string; status: string; scheduled_start: string | null; }
interface EvRow { source: string; query: string; rows: number; meaning: string; }
interface Report {
  ok: boolean; generated_at: string; health: Health;
  blockers: Blocker[]; blockers_summary: { total: number; escalation: number };
  handoffs: Handoff[];
  queues: { council_pending: number; board_scheduled: number; council: CouncilRow[]; board: BoardRow[] };
  evidence_trail: EvRow[];
}

let _data: Report | null = null;
let _err: string | null = null;

export async function loadNexus(): Promise<void> {
  _err = null;
  try {
    const res = await apiGetJson<Report>('/castintech-api/nexus/report');
    if (res.ok) { _data = res.data; }
    else { _err = res.error; _data = null; }
  }
  catch (e) { _err = (e as Error).message; _data = null; }
}

export function renderNexus(): void {
  const root = document.getElementById('view-nexus');
  if (!root) return;
  if (_err) { root.innerHTML = `<div class="gb-card"><div class="gb-empty-sm">Nexus raporu alınamadı: ${escHtml(_err)}</div></div>`; return; }
  if (!_data) { root.innerHTML = `<div class="gb-card"><div class="gb-empty-sm">Yükleniyor…</div></div>`; return; }
  root.innerHTML = html(_data);
}

// Durum → renk (MOR YOK): active=yeşil, degraded=amber, unknown=gri.
const STATUS = {
  active: { c: '#16a34a', bg: 'rgba(22,163,74,.12)', t: 'AKTİF (kanıtlı)' },
  degraded: { c: '#d97706', bg: 'rgba(217,119,6,.12)', t: 'DEGRADED' },
  unknown: { c: '#6b7280', bg: 'rgba(107,114,128,.14)', t: 'BİLİNMİYOR' },
};

function fmtDate(s: string | null): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }); } catch { return s; }
}

function pill(text: string, color: string, bg: string): string {
  return `<span style="display:inline-block;padding:2px 10px;border-radius:999px;font-size:12px;font-weight:700;color:${color};background:${bg}">${escHtml(text)}</span>`;
}

function healthCard(h: Health, generatedAt: string): string {
  const st = STATUS[h.status] || STATUS.unknown;
  const reasons = h.reasons.map(r => `<li>${escHtml(r)}</li>`).join('');
  const ev = h.evidence.map(e =>
    `<tr><td>${escHtml(e.kind)}</td><td><code>${escHtml(e.ref)}</code></td><td>${fmtDate(e.at)}</td><td>${escHtml(e.detail || '')}</td></tr>`
  ).join('');
  return `<div class="gb-card">
    <div class="gb-header"><span class="gb-card-title">🧭 Nexus Health</span>
      <span class="gb-card-sub">üretim: ${fmtDate(generatedAt)}</span></div>
    <div style="display:flex;align-items:center;gap:12px;margin:8px 0">
      ${pill(st.t, st.c, st.bg)}
      <span class="gb-card-sub">kanıt: ${h.evidence.length} kayıt</span>
    </div>
    <ul style="margin:6px 0 10px 18px;font-size:13px;color:var(--text2)">${reasons}</ul>
    <table class="gb-table"><thead><tr><th>kanıt türü</th><th>ref</th><th>zaman</th><th>detay</th></tr></thead>
      <tbody>${ev || '<tr><td colspan="4" class="gb-empty-sm">kanıt yok</td></tr>'}</tbody></table>
  </div>`;
}

function blockerBoard(blockers: Blocker[], sum: { total: number; escalation: number }): string {
  const sorted = [...blockers].sort((a, b) => Number(b.escalation_needed) - Number(a.escalation_needed) || b.age_ms - a.age_ms);
  const rows = sorted.map(b => {
    const esc = b.escalation_needed
      ? pill('ESKALE', '#dc2626', 'rgba(220,38,38,.12)')
      : pill('izlemede', '#6b7280', 'rgba(107,114,128,.14)');
    const extKey = b.external_key ? `<span class="gb-card-sub" style="font-size:10px"> · ${escHtml(b.external_key)}</span>` : '';
    return `<tr${b.escalation_needed ? ' style="background:rgba(220,38,38,.05)"' : ''}>
      <td><code title="db_id: ${escHtml(b.db_id || '—')}">${escHtml(b.display_key || b.issue_id)}</code>${extKey}</td>
      <td>${escHtml(b.owner)}</td>
      <td>${escHtml(b.reason)}</td>
      <td>${escHtml(b.age)}</td>
      <td>${escHtml(b.waiting_on)}</td>
      <td>${fmtDate(b.last_comment_at)}</td>
      <td><strong>${escHtml(b.next_action)}</strong></td>
      <td>${esc}</td></tr>`;
  }).join('');
  return `<div class="gb-card">
    <div class="gb-header"><span class="gb-card-title">🚧 Blocker Board</span>
      <span class="gb-card-sub">${sum.total} blocker · ${sum.escalation} eskalasyon</span></div>
    <table class="gb-table"><thead><tr>
      <th>issue_id</th><th>owner</th><th>reason</th><th>age</th><th>waiting_on</th>
      <th>last_comment</th><th>next_action</th><th>escalation</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="8" class="gb-empty-sm">blocker yok</td></tr>'}</tbody></table>
  </div>`;
}

function handoffFlow(hs: Handoff[]): string {
  const rows = hs.map(h =>
    `<tr><td>${fmtDate(h.at)}</td><td>${escHtml(h.from || '—')}</td><td>${escHtml(h.title || '')}</td>
      <td><code>${escHtml(h.issue || '—')}</code></td><td>${escHtml(h.detail)}</td></tr>`
  ).join('');
  return `<div class="gb-card">
    <div class="gb-header"><span class="gb-card-title">🔀 Handoff Flow</span>
      <span class="gb-card-sub">son ${hs.length} devir</span></div>
    <table class="gb-table"><thead><tr><th>zaman</th><th>kimden</th><th>başlık</th><th>issue</th><th>detay</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="5" class="gb-empty-sm">handoff kaydı yok</td></tr>'}</tbody></table>
  </div>`;
}

function queues(q: Report['queues']): string {
  const council = q.council.map(c =>
    `<tr><td>${escHtml(c.decision_type || '')}</td><td>${escHtml(c.subject_type || '')}</td>
      <td>${escHtml(c.risk_level || '')}</td><td>${escHtml(c.status)}</td><td>${fmtDate(c.created_at)}</td></tr>`
  ).join('');
  const board = q.board.map(b =>
    `<tr><td>${escHtml(b.type || '')}</td><td>${escHtml(b.title || '')}</td><td>${escHtml(b.status)}</td><td>${fmtDate(b.scheduled_start)}</td></tr>`
  ).join('');
  return `<div class="gb-card">
    <div class="gb-header"><span class="gb-card-title">🗳️ Board / Council Queue</span>
      <span class="gb-card-sub">Council ${q.council_pending} · Board ${q.board_scheduled}</span></div>
    <div style="font-size:12px;font-weight:600;color:var(--text2);margin:6px 0 4px">Council (karar bekleyen)</div>
    <table class="gb-table"><thead><tr><th>tip</th><th>konu</th><th>risk</th><th>durum</th><th>açılış</th></tr></thead>
      <tbody>${council || '<tr><td colspan="5" class="gb-empty-sm">bekleyen council kararı yok</td></tr>'}</tbody></table>
    <div style="font-size:12px;font-weight:600;color:var(--text2);margin:12px 0 4px">Board (planlı toplantı)</div>
    <table class="gb-table"><thead><tr><th>tip</th><th>başlık</th><th>durum</th><th>tarih</th></tr></thead>
      <tbody>${board || '<tr><td colspan="4" class="gb-empty-sm">planlı board toplantısı yok</td></tr>'}</tbody></table>
  </div>`;
}

function evidenceTrail(ev: EvRow[]): string {
  const rows = ev.map(e =>
    `<tr><td><code>${escHtml(e.source)}</code></td><td><code>${escHtml(e.query)}</code></td>
      <td>${e.rows}</td><td>${escHtml(e.meaning)}</td></tr>`
  ).join('');
  return `<div class="gb-card">
    <div class="gb-header"><span class="gb-card-title">🔎 Evidence Trail</span>
      <span class="gb-card-sub">her iddianın DB kanıtı</span></div>
    <table class="gb-table"><thead><tr><th>kaynak</th><th>sorgu</th><th>satır</th><th>anlam</th></tr></thead>
      <tbody>${rows}</tbody></table>
  </div>`;
}

function html(d: Report): string {
  return healthCard(d.health, d.generated_at)
    + blockerBoard(d.blockers, d.blockers_summary)
    + handoffFlow(d.handoffs)
    + queues(d.queues)
    + evidenceTrail(d.evidence_trail);
}
